import { useState } from "react";
import { ChevronDown, ClipboardList } from "lucide-react";

export interface ReviewItem {
  id: string;
  label: string;
}

interface ReviewItemsPanelProps {
  items: ReviewItem[];
}

/**
 * Internal sign-off helper. Renders only in development builds and lists every
 * element on the page currently marked "TO CONFIRM". Remove before launch.
 */
const ReviewItemsPanel = ({ items }: ReviewItemsPanelProps) => {
  const [open, setOpen] = useState(false);

  if (!import.meta.env.DEV || items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[260px] max-w-[calc(100vw-2rem)] print:hidden">
      <div className="rounded-xl border border-accent-yellow bg-white shadow-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="review-items-list"
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left focus-ring-sky"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
            <ClipboardList size={16} aria-hidden="true" />
            Review items
            <span className="rounded-full bg-accent-yellow/25 px-2 py-0.5 text-[11px] font-bold text-brand-navy">
              {items.length}
            </span>
          </span>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`shrink-0 text-brand-navy transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <ul id="review-items-list" className="border-t border-border max-h-[40vh] overflow-y-auto">
            {items.map((item) => (
              <li key={item.id} className="border-b border-border last:border-0">
                <a
                  href={`#${item.id}`}
                  className="block px-4 py-2.5 text-[13px] leading-snug text-ink-muted hover:bg-surface-alt hover:text-brand-navy focus-ring-sky"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ReviewItemsPanel;
